/*
This is a React component that renders a data table. It takes in props for data, columns, action items, and an 
onRowClick callback. The component uses the Chakra UI library for styling and React Icons for icons. 

The component uses the useState hook to store state for filteredData, searchTerm, recordsPerPage, currentPage, 
sortDirection and sortedColumn. It also has functions to handle sorting (handleSort), searching (handleSearch), c
learing search (handleClearSearch), changing records per page (handleRecordsPerPage), navigating pages 
(handleNextPage, handlePreviousPage and handleSelectPage) and clicking on rows (handleRowClick). 

The component renders a table with a header containing the column names. If sorting is enabled on a column 
it will render an up or down arrow icon next to it depending on the sort direction. The body of the table 
contains the data from props.data filtered by searchTerm if set. If actionItems are passed in as props they 
will be rendered as buttons in the last column of each row.
*/

import React, { useState, useEffect } from 'react';
import {
    Table,
    Tbody,
    Td,
    Thead,
    Th,
    Tr,
    Text,
    Input,
    Button,
    Icon,
    IconButton,
    Select,
    InputGroup,
    InputRightElement,
    InputLeftElement,
    Box,
    Flex,
} from '@chakra-ui/react';
import {
    FaSearch,
    FaTimesCircle,
    FaSortUp,
    FaSortDown,
    FaChevronLeft,
    FaChevronRight,
} from 'react-icons/fa';

export interface DataProp {
    [key: string]: any | undefined;
}

interface ColumnConfig {
    name: string;
    key: string;
    transform?: Function;
    sorted?: boolean;
    sortDirection?: string;
}

interface CallbackProp {
    label: string;
    callback: (data: DataProp) => void;
    icon: any;
    colour: string;
}

interface Props {
    data: DataProp[];
    recordsPerPage?: number;
    columns: ColumnConfig[];
    actionItems?: CallbackProp[];
    onRowClick?: (data: DataProp) => void;
    // width?: string;
}

const DataTable: React.FC<Props> = ({
    data,
    recordsPerPage: _recordsPerPage,
    columns,
    actionItems,
    onRowClick,
    // width,
}) => {
    const [filteredData, setFilteredData] = useState<DataProp[]>(data);
    const [searchTerm, setSearchTerm] = useState('');
    const [recordsPerPage, setRecordsPerPage] = useState(_recordsPerPage || 5);
    const [currentPage, setCurrentPage] = useState(1);

    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [sortedColumn, setSortedColumn] = useState<string | undefined>(undefined);

    const numberOfRecords = filteredData.length;
    const numberOfPages = Math.ceil(filteredData.length / recordsPerPage);

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    // used to grab values in dot notation from objects
    // ie a key in the columns can be "property.subproperty" and using this
    // function will return the value on the object
    const getObjValue = (obj: any, key: string, transform?: Function) => {
        const val = key.split('.').reduce((o, i) => o[i], obj);
        if (!transform) {
            return val;
        }
        return transform(val);
    };

    const handleSearch = () => {
        //filter by search input
        let fData = data.filter((item) =>
            columns.some((column) => {
                const itemValue = getObjValue(item, column.key);
                return itemValue.toString().toLowerCase().includes(searchTerm.toLowerCase());
            }),
        );
        // re-sort if column already sorted
        if (sortedColumn) {
            fData = fData.sort((a, b) => sortFn(a, b, sortedColumn, sortDirection));
        }
        setFilteredData(fData);
        setCurrentPage(1);
    };

    const handleChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    useEffect(() => {
        handleSearch();
    }, [searchTerm]);

    const sortFn = (a: DataProp, b: DataProp, key: string, sDirection: 'asc' | 'desc') => {
        const aValue = getObjValue(a, key).toString().toLowerCase();
        const bValue = getObjValue(b, key).toString().toLowerCase();

        return sDirection === 'asc' ? (aValue > bValue ? 1 : -1) : aValue < bValue ? 1 : -1;
    };

    const handleSort = (key: string) => {
        const direction =
            sortedColumn && sortedColumn === key
                ? sortDirection === 'asc'
                    ? 'desc'
                    : 'asc'
                : sortDirection;

        setSortDirection(direction);
        setSortedColumn(key);

        setFilteredData([...filteredData].sort((a, b) => sortFn(a, b, key, direction)));
    };

    const handleRecordsPerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setRecordsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const handleNextPage = () => {
        setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        setCurrentPage(currentPage - 1);
    };

    const handleSelectPage = (page: number) => {
        setCurrentPage(page);
    };

    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;

    const handleRowClick = (e: any, row: any) => {
        onRowClick && onRowClick(row);
    };

    // TODO(DEVPOR32-13): Restrict column widths so that long descriptions don't make the whole table go past width of page

    return (
        <Box>
            <Flex>
                <Text mb={4} flex={'1 0 auto'}>
                    Showing {startIndex + 1} - {endIndex} of {numberOfRecords} records (
                    {currentPage}/{numberOfPages} pages)
                </Text>
            </Flex>

            <Flex justifyContent={'space-between'}>
                <Flex alignItems={'center'}>
                    <Text as="label" mr={2}>
                        Show
                    </Text>
                    <Select onChange={handleRecordsPerPage} value={recordsPerPage}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                    </Select>
                    <Text as="label" ml={2}>
                        configs
                    </Text>
                </Flex>
                <Box>
                    <InputGroup>
                        <InputLeftElement
                            pointerEvents="none"
                            children={<FaSearch color="gray.300" />}
                        />
                        <Input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleChangeSearch}
                            mb={4}
                            data-cy="search-input"
                        />
                        <InputRightElement>
                            <IconButton
                                aria-label={'test'}
                                icon={<FaTimesCircle />}
                                h="1.75rem"
                                size="sm"
                                onClick={handleClearSearch}
                            ></IconButton>
                        </InputRightElement>
                    </InputGroup>
                </Box>
            </Flex>
            <Table variant="striped">
                <Thead backgroundColor={'lightgray'}>
                    <Tr>
                        {columns.map((column) => (
                            <Th
                                key={column.key}
                                onClick={() => handleSort(column.key)}
                                textAlign="center"
                            >
                                {column.name}
                                {sortedColumn === column.key && (
                                    <Icon
                                        data-cy={sortDirection}
                                        as={sortDirection === 'asc' ? FaSortUp : FaSortDown}
                                    ></Icon>
                                )}
                            </Th>
                        ))}
                        {actionItems && (
                            <Th textAlign="center">
                                <Text>Actions</Text>
                            </Th>
                        )}
                    </Tr>
                </Thead>
                <Tbody>
                    {filteredData.slice(startIndex, endIndex).map((item, index) => (
                        <Tr key={index} onClick={(e) => handleRowClick(e, item)} row-id={index}>
                            {columns.map((column) => (
                                <Td key={column.key} col-id={column.key}>
                                    <Flex
                                        justify={'center'}
                                        alignItems={'center'}
                                        textAlign={'center'}
                                    >
                                        {getObjValue(item, column.key, column.transform)}
                                    </Flex>
                                </Td>
                            ))}

                            {actionItems && (
                                <Td textAlign="center">
                                    {actionItems.map((actionItem, idx) => (
                                        <Button
                                            key={actionItem.label}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                actionItem.callback(item);
                                            }}
                                            leftIcon={actionItem.icon}
                                            size="sm"
                                            // marginRight={idx !== actionItems.length ? 5 : 0}
                                            margin={idx !== actionItems.length ? 2 : 0}
                                            colorScheme={actionItem.colour}
                                            w={'100px'}
                                        >
                                            {actionItem.label}
                                        </Button>
                                    ))}
                                </Td>
                            )}
                        </Tr>
                    ))}
                </Tbody>
            </Table>
            <Flex mt={5} alignItems={'center'}>
                <Flex flex={1} alignItems="center" gap="2">
                    <Button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        leftIcon={<FaChevronLeft />}
                    >
                        Previous
                    </Button>
                    <Flex mr={2} ml={2} alignItems={'center'}>
                        <Text mr={2} ml={3}>
                            Page
                        </Text>
                        <Select
                            onChange={(e) => handleSelectPage(Number(e.currentTarget.value))}
                            value={currentPage}
                            disabled={numberOfPages <= 0}
                        >
                            {Array.from(Array(numberOfPages), (_, i) => i + 1).map((page) => (
                                <option key={page} value={page}>
                                    {page}
                                </option>
                            ))}
                        </Select>
                        <Text whiteSpace={'nowrap'} ml={2} mr={3}>
                            of {numberOfPages}
                        </Text>
                    </Flex>
                    <Button
                        onClick={handleNextPage}
                        disabled={endIndex >= filteredData.length}
                        rightIcon={<FaChevronRight />}
                    >
                        Next
                    </Button>
                </Flex>
                <Box>
                    <Text ml="3" mr="3">
                        Showing {startIndex + 1} - {endIndex} of {numberOfRecords} records (
                        {currentPage}/{numberOfPages} pages)
                    </Text>
                </Box>
            </Flex>
        </Box>
    );
};

export default DataTable;
